import { useMemo, useState } from 'react';

import styled from 'styled-components';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import { PAGES } from '../../constants/pages';
import { colors } from 'constants/colors';

import { TrelloCardInfoPopup } from 'pages/SupplyOrders/Popups/TrelloCardInfoPopup';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { SegmentedControl } from 'components/SegmentedControl';
import { FilterBar, type FilterConfig } from 'components/FilterBar';
import { SupplyOrdersTrelloView } from './SupplyOrdersTrelloView';
import { SupplyOrdersTimelineView } from './SupplyOrdersTimelineView';
import { useGetSuppliersQuery, useGetWarehouseColumnsQuery } from 'data/__generated__';
import { useSearchParams } from 'react-router-dom';
import { SUPPLY_ORDER_STATES_LABELS } from './constants/states';

export type FreshnessFilter = 'fresh' | 'sec' | 'all';
export type ViewMode = 'kanban' | 'timeline';

type ParamTypes = {
    supplier?: string;
    filterDelayed?: boolean;
    freshness?: FreshnessFilter[];
    warehouseColumnId?: string;
    state?: string;
    displayLimitDate?: string;
    view?: ViewMode;
};

type UrlParams<T> = { [P in keyof T]?: string[] };

function parseUrlSearchParams<T>(searchParams: URLSearchParams): UrlParams<T> {
    const params: UrlParams<T> = {};
    // @ts-expect-error ignore URLSearchParams is not templated, key is string even with Location<T>
    searchParams.forEach((value: string, key: keyof T) => {
        if (!params[key]) {
            params[key] = [value];
        } else {
            (params[key] as string[]).push(value);
        }
    });
    return params;
}

function buildUrlSearchParams<T>(params: UrlParams<T>): URLSearchParams {
    return Object.entries(params).reduce((urlSearchParams, [key, values]) => {
        (values as string[]).forEach((value) => urlSearchParams.append(key, value));
        return urlSearchParams;
    }, new URLSearchParams());
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function getDefaultDisplayLimitDate(): string {
    const date = new Date(Date.now() - ONE_WEEK_MS);
    return date.toISOString().split('T')[0] as string;
}

const STATE_OPTIONS = Object.entries(SUPPLY_ORDER_STATES_LABELS).map(([value, label]) => ({
    value,
    label,
}));

export const SupplyOrders = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [trelloCardInfoPopup, setTrelloCardInfoPopup] = useState(false);

    const params = parseUrlSearchParams<ParamTypes>(searchParams);

    const viewMode: ViewMode = (params.view?.[0] as ViewMode) || 'kanban';
    const defaultDisplayLimitDate = useMemo(() => getDefaultDisplayLimitDate(), []);

    // Data queries for filter options
    const {
        data: suppliersData,
        loading: suppliersLoading,
        error: suppliersError,
    } = useGetSuppliersQuery({
        fetchPolicy: 'cache-and-network',
    });
    const { data: warehouseColumnsData } = useGetWarehouseColumnsQuery();

    if (!suppliersLoading && (suppliersError || !suppliersData)) {
        throw new Error('Une erreur est survenue lors de la récupération des fournisseurs');
    }

    const supplierOptions = useMemo(
        () => (suppliersData?.suppliers || []).map((s) => ({ label: s.name, value: s._id })),
        [suppliersData],
    );

    const warehouseOptions = useMemo(
        () => (warehouseColumnsData?.warehouseColumns || []).map((w) => ({ label: w.name, value: w._id })),
        [warehouseColumnsData],
    );

    // Build filter state from URL params
    const filterState: Record<string, unknown> = {
        supplier: params.supplier && params.supplier.length > 0 ? params.supplier : undefined,
        warehouseColumnId:
            params.warehouseColumnId && params.warehouseColumnId.length > 0 ? params.warehouseColumnId : undefined,
        state: params.state && params.state.length > 0 ? params.state : undefined,
        filterDelayed: params.filterDelayed?.includes('true') ? true : undefined,
        displayLimitDate: params.displayLimitDate?.[0] || defaultDisplayLimitDate,
    };

    // Derived values for child components
    const supplierIds = (filterState.supplier as string[] | undefined) || [];
    const warehouseColumnId = (filterState.warehouseColumnId as string[] | undefined)?.[0] || null;
    const stateFilter = (filterState.state as string[] | undefined) || [];
    const filterDelayed = (filterState.filterDelayed as boolean | undefined) || false;
    const displayLimitDateStr = (filterState.displayLimitDate as string | undefined) || getDefaultDisplayLimitDate();
    const displayLimitDate = new Date(displayLimitDateStr);

    function updateFilter(key: string, value: unknown) {
        const newParams = { ...params };
        if (value === undefined) {
            delete newParams[key as keyof ParamTypes];
        } else if (Array.isArray(value)) {
            (newParams as Record<string, string[]>)[key] = value;
        } else if (typeof value === 'boolean') {
            (newParams as Record<string, string[]>)[key] = [String(value)];
        } else {
            (newParams as Record<string, string[]>)[key] = [String(value)];
        }
        setSearchParams(buildUrlSearchParams(newParams));
    }

    function clearFilters() {
        // Keep only view param
        const newParams: UrlParams<ParamTypes> = {};
        if (params.view) {
            newParams.view = params.view;
        }
        setSearchParams(buildUrlSearchParams(newParams));
    }

    function updateViewMode(view: ViewMode) {
        setSearchParams(buildUrlSearchParams({ ...params, view: [view] }));
    }

    const filters: FilterConfig[] = [
        {
            key: 'supplier',
            title: 'Fournisseur',
            type: 'multi-select',
            options: supplierOptions,
        },
        {
            key: 'warehouseColumnId',
            title: 'Entrepôt',
            type: 'select',
            options: warehouseOptions,
        },
        {
            key: 'state',
            title: 'État',
            type: 'multi-select',
            options: STATE_OPTIONS,
        },
        {
            key: 'filterDelayed',
            title: 'En retard',
            type: 'boolean',
        },
        {
            key: 'displayLimitDate',
            title: 'Date limite rangés/annulés',
            type: 'date',
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.supplyOrders} />
                </HeaderTitle>
                <HeaderActions>
                    <SegmentedControl
                        options={[
                            { value: 'kanban' as const, label: 'Kanban' },
                            { value: 'timeline' as const, label: 'Timeline' },
                        ]}
                        value={viewMode}
                        onChange={updateViewMode}
                    />
                    <InfoIcon size={25} onClick={() => setTrelloCardInfoPopup(true)} />
                </HeaderActions>
            </Header>
            <FilterBar
                filters={filters}
                filterState={filterState}
                updateFilter={updateFilter}
                clearFilters={clearFilters}
            />
            <Content>
                {viewMode === 'kanban' ? (
                    <SupplyOrdersTrelloView
                        displayLimitDate={displayLimitDate}
                        supplierIds={supplierIds}
                        filterDelayed={filterDelayed}
                        freshnessFilter="all"
                        warehouseColumnId={warehouseColumnId}
                        stateFilter={stateFilter}
                    />
                ) : (
                    <SupplyOrdersTimelineView
                        displayLimitDate={displayLimitDate}
                        supplierIds={supplierIds}
                        filterDelayed={filterDelayed}
                        freshnessFilter="all"
                        warehouseColumnId={warehouseColumnId}
                        stateFilter={stateFilter}
                    />
                )}
            </Content>
            <TrelloCardInfoPopup setIsOpen={setTrelloCardInfoPopup} isOpen={trelloCardInfoPopup} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-left: auto;
`;

const InfoIcon = styled(AiOutlineInfoCircle)`
    cursor: pointer;
    color: ${colors.blue};
    z-index: 1;

    &:hover {
        transform: scale(1.1);
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 5px 5px;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
